import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Map as MapIcon, ZoomIn, ZoomOut, Navigation } from "lucide-react";
import {
  DeliveryOrder,
  AssignedCart,
  Group,
} from "../../../../redux/deliveryRouteOptimizer";
import "./index.css";
import { useGoogleMaps } from "../../../../hooks/useGoogleMaps";
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  DEPOT_LOCATION,
  STAR_PATH,
} from "../../../../constants/constans";
import { Route, useDirectionsService } from "../../../../hooks/directServiceHook";

interface GoogleMapViewProps {
  deliveries: DeliveryOrder[];
  vehicles: AssignedCart[];
  routes: Record<string, string[]>;
  groups: Record<string, Group>;
  selectedDeliveries: string[];
  hoveredDelivery: string | null;
  setHoveredDelivery: (id: string | null) => void;
  toggleDeliverySelection: (id: string) => void;
  workflowStep: "group" | "assign" | "actual";
  showAllVehicles: boolean;
  selectedVehicleFilter: string[];
  getDeliveryGroup: (id: string) => Group | undefined;
  selectedOrdersForMap: string[];
  showOnlySelectedOrders: boolean;
  groupTypeFilter?: "tentative" | "finalized";
  tentativeFilter?: "all" | "assigned" | "unassigned";
  selectedCartsForMap?: string[];
  showOnlySelectedCarts?: boolean;
  selectedGroupsForMap?: string[];
  showOnlySelectedGroups?: boolean;
}

// Custom hook for route caching
const useRouteCache = () => {
  const routeCacheRef = useRef<Map<string, Route>>(new Map());
  
  const getCachedRoute = useCallback((key: string): Route | null => {
    return routeCacheRef.current.get(key) || null;
  }, []);

  const setCachedRoute = useCallback((key: string, route: Route) => {
    routeCacheRef.current.set(key, route);
  }, []);

  const clearCache = useCallback(() => {
    routeCacheRef.current.clear();
  }, []);

  return { getCachedRoute, setCachedRoute, clearCache };
};

const GoogleMapView: React.FC<GoogleMapViewProps> = ({
  deliveries,
  vehicles,
  groups,
  selectedDeliveries,
  hoveredDelivery,
  setHoveredDelivery,
  toggleDeliverySelection,
  workflowStep,
  showAllVehicles,
  selectedVehicleFilter,
  getDeliveryGroup,
  selectedOrdersForMap,
  showOnlySelectedOrders,
  groupTypeFilter = "tentative",
  tentativeFilter = "all",
  selectedCartsForMap = [],
  showOnlySelectedCarts = false,
  selectedGroupsForMap = [],
  showOnlySelectedGroups = false,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const polylinesRef = useRef<google.maps.Polyline[]>([]);
  const depotMarkerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const depotInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const routeCalculationRef = useRef<NodeJS.Timeout>();
  const previousDeliveryIdsRef = useRef<string>('');

  const [selectedGroupFilter, setSelectedGroupFilter] = useState<string | null>(
    null
  );
  const { isLoaded } = useGoogleMaps();
  const { getRoute } = useDirectionsService();
  const { getCachedRoute, setCachedRoute, clearCache } = useRouteCache();

  // Determine effective filters based on workflow step
  const effectiveGroupTypeFilter = useMemo(() => {
    if (workflowStep === "actual") {
      return "tentative";
    }
    return groupTypeFilter;
  }, [workflowStep, groupTypeFilter]);

  const effectiveTentativeFilter = useMemo(() => {
    if (workflowStep === "actual") {
      return "assigned";
    }
    return tentativeFilter;
  }, [workflowStep, tentativeFilter]);

  // Helper function to check if coordinates are approximately the same
  const areCoordinatesSimilar = useCallback((
    coord1: google.maps.LatLngLiteral,
    coord2: google.maps.LatLngLiteral,
    threshold = 0.0001 // ~11 meters
  ): boolean => {
    const latDiff = Math.abs(coord1.lat - coord2.lat);
    const lngDiff = Math.abs(coord1.lng - coord2.lng);
    return latDiff < threshold && lngDiff < threshold;
  }, []);

  // Helper function to generate slight offset for overlapping markers
  const generateOffset = useCallback((index: number, total: number): google.maps.LatLngLiteral => {
    if (total <= 1) return { lat: 0, lng: 0 };
    
    // Create a circular arrangement
    const radius = 0.00015; // ~17 meters
    const angle = (2 * Math.PI * index) / total;
    
    return {
      lat: radius * Math.cos(angle),
      lng: radius * Math.sin(angle)
    };
  }, []);

  // Helper function to get delivery position
  const getDeliveryPosition = useCallback(
    (delivery: DeliveryOrder): google.maps.LatLngLiteral | null => {
      const lat =
        delivery.lat || delivery.deliveryDetails?.coordinates?.latitude;
      const lng =
        delivery.lng || delivery.deliveryDetails?.coordinates?.longitude;

      if (!lat || !lng) {
        return null;
      }

      return { lat, lng };
    },
    []
  );

  // Helper function to identify P&S (Perera & Sons) orders
  const isPnSOrder = useCallback((delivery: DeliveryOrder): boolean => {
    return Boolean(delivery.organization?.displayName?.includes("P&S"));
  }, []);

  // Helper function to group deliveries by similar coordinates
  const groupDeliveriesByLocation = useCallback((
    deliveries: DeliveryOrder[],
    threshold = 0.0001
  ): Map<string, DeliveryOrder[]> => {
    const locationGroups = new Map<string, DeliveryOrder[]>();
    
    deliveries.forEach(delivery => {
      const position = getDeliveryPosition(delivery);
      if (!position) return;
      
      let foundGroup = false;
      
      // Check if this delivery belongs to any existing group
      for (const [key, group] of locationGroups.entries()) {
        const [latStr, lngStr] = key.split(',');
        const groupCenter: google.maps.LatLngLiteral = {
          lat: parseFloat(latStr),
          lng: parseFloat(lngStr)
        };
        
        if (areCoordinatesSimilar(position, groupCenter, threshold)) {
          group.push(delivery);
          foundGroup = true;
          break;
        }
      }
      
      // If no existing group found, create a new one
      if (!foundGroup) {
        locationGroups.set(`${position.lat},${position.lng}`, [delivery]);
      }
    });
    
    return locationGroups;
  }, [getDeliveryPosition, areCoordinatesSimilar]);

  // Helper function to calculate center of multiple coordinates
  const calculateCenter = useCallback((coords: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral => {
    if (coords.length === 0) return DEPOT_LOCATION;
    
    const sum = coords.reduce(
      (acc, coord) => {
        acc.lat += coord.lat;
        acc.lng += coord.lng;
        return acc;
      },
      { lat: 0, lng: 0 }
    );
    
    return {
      lat: sum.lat / coords.length,
      lng: sum.lng / coords.length
    };
  }, []);

  // Memoized filtered deliveries - UPDATED WITH deliveryCartId
  const deliveriesToShow = useMemo(() => {
    let filtered = deliveries.filter((delivery) => {
      const deliveryGroup = getDeliveryGroup(delivery.id);
      const isCollectionGroup = deliveryGroup?.isCollectionGrouped;

      // 1. Apply group selection filter first (highest priority)
      if (showOnlySelectedGroups && selectedGroupsForMap.length > 0) {
        const isInSelectedGroup = deliveryGroup && selectedGroupsForMap.includes(deliveryGroup.id);
        return isInSelectedGroup;
      }

      // 2. Handle cart selection filtering - UPDATED WITH deliveryCartId
      if (showOnlySelectedCarts && selectedCartsForMap.length > 0) {
        // Check if delivery belongs to any group assigned to selected carts using deliveryCartId
        const isInSelectedCartGroup = Object.values(groups).some(
          (group) => 
            group.cart?.deliveryCartId && 
            selectedCartsForMap.includes(group.cart.deliveryCartId) &&
            group.deliveryIds.includes(delivery.id)
        );
        
        // Check if delivery is directly assigned to selected cart
        const isDirectlyAssignedToCart = 
          delivery.vehicleId && 
          selectedCartsForMap.includes(String(delivery.vehicleId));
        
        // Always include if delivery is in selected orders
        const isSelectedOrder = selectedOrdersForMap.includes(delivery.id);
        
        // Return true if any of these conditions are met
        return isInSelectedCartGroup || isDirectlyAssignedToCart || isSelectedOrder;
      }

      // 3. Handle order selection filter
      if (showOnlySelectedOrders && selectedOrdersForMap.length > 0) {
        return selectedOrdersForMap.includes(delivery.id);
      }

      // 4. Handle different workflow steps with CLEAR SEPARATION
      if (workflowStep === "actual" || effectiveGroupTypeFilter === "finalized") {
        // ACTUAL/FINALIZED STEP: Only show collection groups
        if (selectedGroupFilter) {
          const cartGroups = Object.values(groups).filter(
            (group) =>
              group.cart?.deliveryCartId === selectedGroupFilter &&
              group.isCollectionGrouped
          );
          const isInSelectedCart = cartGroups.some(
            (group) => group.id === deliveryGroup?.id
          );
          return isInSelectedCart && isCollectionGroup === true;
        }
        return isCollectionGroup === true;
      } 
      else if (workflowStep === "group") {
        // GROUP STEP: Only show unassigned and tentative groups (non-collection)
        if (selectedGroupFilter) {
          if (selectedGroupFilter === "unassigned") {
            return (
              delivery.status === "unassigned" &&
              !deliveryGroup &&
              !delivery.groupedForCollection
            );
          } else {
            return (
              deliveryGroup?.id === selectedGroupFilter && 
              !isCollectionGroup
            );
          }
        } else {
          return (
            (delivery.status === "unassigned" &&
              !delivery.groupedForCollection) ||
            (delivery.status === "grouped" &&
              !!deliveryGroup &&
              !isCollectionGroup)
          );
        }
      } 
      else {
        // ASSIGN STEP: Handle tentative filter with CLEAR SEPARATION
        switch (effectiveTentativeFilter) {
          case "all":
            if (showAllVehicles) {
              return (
                (delivery.status === "unassigned" &&
                  !delivery.groupedForCollection) ||
                (delivery.status === "grouped" &&
                  !!deliveryGroup &&
                  !deliveryGroup.isCollectionGrouped) ||
                (delivery.status === "assigned" &&
                  !!deliveryGroup &&
                  !deliveryGroup.isCollectionGrouped)
              );
            } else {
              const isInSelectedCart = 
                delivery.vehicleId && 
                selectedVehicleFilter.includes(String(delivery.vehicleId));
              
              const isGroupInSelectedCart = deliveryGroup?.cart?.deliveryCartId && 
                selectedVehicleFilter.includes(deliveryGroup.cart.deliveryCartId);
              
              return (
                (delivery.status === "unassigned" && !delivery.groupedForCollection) ||
                (delivery.status === "grouped" && !!deliveryGroup && !deliveryGroup.isCollectionGrouped && isGroupInSelectedCart) ||
                (delivery.status === "assigned" && isInSelectedCart && deliveryGroup && !deliveryGroup.isCollectionGrouped)
              );
            }
          
          case "assigned":
            // IN ASSIGN STEP: Show groups assigned to carts
            if (showAllVehicles) {
              return (delivery.status === "assigned" || 
                (!!deliveryGroup?.cart?.deliveryCartId && !deliveryGroup.isCollectionGrouped));
            } else {
              const isInSelectedCart = 
                delivery.vehicleId && 
                selectedVehicleFilter.includes(String(delivery.vehicleId));
              
              const isGroupInSelectedCart = deliveryGroup?.cart?.deliveryCartId && 
                selectedVehicleFilter.includes(deliveryGroup.cart.deliveryCartId);
              
              return (
                (delivery.status === "assigned" && isInSelectedCart && deliveryGroup && !deliveryGroup.isCollectionGrouped) ||
                (!!deliveryGroup?.cart?.deliveryCartId && !deliveryGroup.isCollectionGrouped && isGroupInSelectedCart)
              );
            }
          
          case "unassigned":
            if (selectedGroupFilter) {
              if (selectedGroupFilter === "unassigned") {
                return (
                  delivery.status === "unassigned" &&
                  !deliveryGroup &&
                  !delivery.groupedForCollection
                );
              } else {
                return (
                  deliveryGroup?.id === selectedGroupFilter && 
                  !deliveryGroup.isCollectionGrouped &&
                  !deliveryGroup.cart?.deliveryCartId
                );
              }
            } else {
              return (
                (delivery.status === "unassigned" &&
                  !deliveryGroup &&
                  !delivery.groupedForCollection) ||
                (delivery.status === "grouped" &&
                  !!deliveryGroup &&
                  !deliveryGroup.isCollectionGrouped &&
                  !deliveryGroup.cart?.deliveryCartId)
              );
            }
          
          default:
            return false;
        }
      }
    });

    return filtered;
  }, [
    deliveries,
    workflowStep,
    selectedGroupFilter,
    showAllVehicles,
    selectedVehicleFilter,
    groups,
    getDeliveryGroup,
    effectiveGroupTypeFilter,
    effectiveTentativeFilter,
    showOnlySelectedOrders,
    selectedOrdersForMap,
    showOnlySelectedCarts,
    selectedCartsForMap,
    showOnlySelectedGroups, 
    selectedGroupsForMap,
  ]);

  // Reset cart filtering when workflow step or filters change
  useEffect(() => {
    if (workflowStep !== "assign" || effectiveGroupTypeFilter === "finalized") {
      setSelectedGroupFilter(null);
    }
  }, [workflowStep, effectiveGroupTypeFilter]);

  // Get groups by cart (both regular and collection) - UPDATED WITH deliveryCartId
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getGroupsByCart = useCallback(
    (cartId: string): Group[] => {
      return Object.values(groups).filter((group) => group.cart?.deliveryCartId === cartId);
    },
    [groups]
  );

  // Helper function to determine if routes should be redrawn
  const shouldRedrawRoutes = useCallback(() => {
    // Get current visible delivery IDs
    const currentDeliveryIds = deliveriesToShow.map(d => d.id).sort().join(',');
    const shouldRedraw = currentDeliveryIds !== previousDeliveryIdsRef.current;
    
    if (shouldRedraw) {
      previousDeliveryIdsRef.current = currentDeliveryIds;
    }
    
    return shouldRedraw;
  }, [deliveriesToShow]);

  // Create depot marker
  const createDepotMarker = useCallback((map: google.maps.Map) => {
    if (depotMarkerRef.current) {
      depotMarkerRef.current.setMap(map);
      return;
    }

    const newDepotMarker = new google.maps.Marker({
      position: DEPOT_LOCATION,
      map,
      icon: {
        url:
          "data:image/svg+xml;base64," +
          btoa(`
          <svg width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="depotGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <path d="M12 2L8 8H2L7 12L5 20L12 16L19 20L17 12L22 8H16L12 2Z" fill="#10B981" stroke="white" stroke-width="1.5" filter="url(#depotGlow)"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20),
      },
      title: "Curry King Depot",
      zIndex: 1000,
    });

    const depotInfoWindow = new google.maps.InfoWindow({
      content: `
        <div class="p-2">
          <h3 class="font-semibold text-green-600">DEPOT</h3>
          <p class="text-sm text-gray-600">Curry King Central Distribution Center</p>
          <p class="text-xs text-gray-500">Colombo, Sri Lanka</p>
        </div>
      `,
    });

    newDepotMarker.addListener("click", () => {
      depotInfoWindow.open(map, newDepotMarker);
    });

    depotMarkerRef.current = newDepotMarker;
    depotInfoWindowRef.current = depotInfoWindow;
  }, []);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;

    const googleMap = new google.maps.Map(mapRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: false,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "on" }],
        },
      ],
    });

    const infoWindowInstance = new google.maps.InfoWindow();
    infoWindowRef.current = infoWindowInstance;

    mapInstanceRef.current = googleMap;

    // Close any open info windows when clicking the map background
    // (marker clicks fire their own "click" event and don't trigger this,
    // so this only fires on genuine "click outside" — exactly what we want)
    googleMap.addListener("click", () => {
      depotInfoWindowRef.current?.close();
      infoWindowRef.current?.close();
    });

    createDepotMarker(googleMap);

    return () => {
      if (depotMarkerRef.current) {
        depotMarkerRef.current.setMap(null);
      }
      markersRef.current.forEach((marker) => marker.setMap(null));
      polylinesRef.current.forEach((polyline) => polyline.setMap(null));
    };
  }, [isLoaded, createDepotMarker]);

  // Ensure depot marker is always on the map when map is available
  // Create star marker icon with glow effect
  const createStarIcon = useCallback(
    (
      color: string,
      isSelected: boolean = false,
      isCollectionGroup: boolean = false,
      isCluster: boolean = false,
      clusterCount: number = 1
    ) => {
      const strokeColor = isSelected ? "#3B82F6" : "white";
      const strokeWidth = isSelected ? 2.5 : 1.5;

      const finalStrokeColor = isCollectionGroup ? "#8B5CF6" : strokeColor;
      const finalStrokeWidth = isCollectionGroup ? 2.5 : strokeWidth;

      // For cluster markers (orange color)
      const clusterColor = "#F97316"; // Orange-500
      const clusterStrokeColor = "#EA580C"; // Orange-600
      
      const finalColor = isCluster ? clusterColor : color;
      const finalStroke = isCluster ? clusterStrokeColor : finalStrokeColor;
      const finalStrokeW = isCluster ? 2.5 : finalStrokeWidth;

      let svgContent = '';
      
      if (isCluster) {
        // Create a different icon for clusters with count badge
        svgContent = `
          <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="clusterGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <circle cx="24" cy="24" r="18" fill="${finalColor}" stroke="${finalStroke}" stroke-width="${finalStrokeW}" filter="url(#clusterGlow)"/>
            <text x="24" y="30" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="14" font-weight="bold">
              ${clusterCount}
            </text>
          </svg>
        `;
      } else {
        // Regular star icon
        svgContent = `
          <svg width="36" height="36" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="starGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            <path d="${STAR_PATH}" 
                  fill="${finalColor}" 
                  stroke="${finalStroke}" 
                  stroke-width="${finalStrokeW}" 
                  filter="url(#starGlow)"/>
          </svg>
        `;
      }

      const size = isCluster ? 48 : 36;
      const anchor = size / 2;

      return {
        url: "data:image/svg+xml;base64," + btoa(svgContent),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(anchor, anchor),
      };
    },
    []
  );

  // Create a distinct marker icon for P&S orders (rounded badge instead of a star)
  const createPnsIcon = useCallback(
    (
      isSelected: boolean = false,
      isCollectionGroup: boolean = false,
      isAssigned: boolean = false
    ) => {
      // Lighter blue while unassigned, darker blue once grouped/assigned/finalized
      const pnsFillColor = isAssigned ? "#1D4ED8" : "#60A5FA";

      // Selection ring uses a color outside the blue family so it stays
      // visible regardless of which blue shade the badge is showing
      const strokeColor = isSelected ? "#F59E0B" : "white";
      const strokeWidth = isSelected ? 2.5 : 1.5;

      const finalStrokeColor = isCollectionGroup ? "#8B5CF6" : strokeColor;
      const finalStrokeWidth = isCollectionGroup ? 2.5 : strokeWidth;

      const svgContent = `
        <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="pnsGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <rect x="2" y="2" width="32" height="32" rx="7"
                fill="${pnsFillColor}"
                stroke="${finalStrokeColor}"
                stroke-width="${finalStrokeWidth}"
                filter="url(#pnsGlow)"/>
          <text x="18" y="23" text-anchor="middle" fill="white"
                font-family="Arial, sans-serif" font-size="10" font-weight="bold">P&amp;S</text>
        </svg>
      `;

      return {
        url: "data:image/svg+xml;base64," + btoa(svgContent),
        scaledSize: new google.maps.Size(36, 36),
        anchor: new google.maps.Point(18, 18),
      };
    },
    []
  );

  // Create delivery info window content (individual delivery) - UPDATED WITH deliveryCartId
  const createDeliveryInfoContent = useCallback(
    (delivery: DeliveryOrder, group?: Group) => {
      const customerName =
        delivery.customer?.displayName ||
        delivery.organization?.displayName ||
        "Unknown";
      const address =
        delivery.deliveryDetails?.address || "No address provided";
      const phoneNumber =
        delivery.customer?.phoneNumber ||
        delivery.organization?.phoneNumber ||
        "No phone number provided";
      const orderReference = delivery.orderReference || "Not mentioned";

      return `
      <div class="p-3 max-w-xs">
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-semibold text-gray-800">${customerName}</h3>
          ${
            delivery.status === "unassigned"
              ? '<span class="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Unassigned</span>'
              : delivery.status === "grouped"
              ? '<span class="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Grouped</span>'
              : '<span class="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Assigned</span>'
          }
        </div>
        
        <div class="space-y-1 text-sm text-gray-600">
          <p><strong>Order Ref:</strong> ${orderReference}</p>
          <p><strong>Phone:</strong> ${phoneNumber}</p>
          <p><strong>Address:</strong> ${address}</p>
          ${
            group
              ? `<p><strong>Group:</strong> <span style="color: ${group.color}">${group.name}</span></p>`
              : ""
          }
          ${
            delivery.vehicleId
              ? `<p><strong>Vehicle:</strong> ${
                  vehicles.find((v) => v.id === String(delivery.vehicleId))
                    ?.name || "Unknown"
                }</p>`
              : ""
          }
          ${
            group?.cart?.deliveryCartId
              ? `<p><strong>Assigned Cart:</strong> ${
                  vehicles.find((v) => v.id === group.cart!.deliveryCartId)
                    ?.name || "Unknown"
                }</p>`
              : ""
          }
        </div>
        
        ${
          workflowStep === "group" && delivery.status === "unassigned"
            ? `
          <div class="mt-3 pt-2 border-t border-gray-200">
            <button onclick="window.deliverySelectionHandler('${delivery.id}')" 
              class="w-full bg-blue-500 hover:bg-blue-600 text-white text-sm py-1 px-3 rounded transition-colors">
              ${
                selectedDeliveries.includes(delivery.id)
                  ? "✓ Selected"
                  : "Select"
              }
            </button>
          </div>
        `
            : ""
        }
      </div>
    `;
    },
    [workflowStep, selectedDeliveries, vehicles]
  );

  // Create cluster info window content
  const createClusterInfoContent = useCallback((deliveries: DeliveryOrder[]) => {
    const ordersList = deliveries.map(delivery => {
      const customerName =
        delivery.customer?.displayName ||
        delivery.organization?.displayName ||
        "Unknown";
      const orderReference = delivery.orderReference || "Not mentioned";
      
      return `
        <div class="py-2 border-b border-gray-200 last:border-b-0">
          <div class="flex items-center justify-between">
            <span class="font-medium text-sm">${orderReference}</span>
            <span class="text-xs text-gray-500">${customerName}</span>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="p-3 max-w-xs">
        <div class="flex items-center mb-3">
          <div class="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center mr-2">
            <span class="text-orange-600 font-bold text-sm">${deliveries.length}</span>
          </div>
          <h3 class="font-semibold text-gray-800">${deliveries.length} Orders at Same Location</h3>
        </div>
        
        <div class="max-h-60 overflow-y-auto">
          ${ordersList}
        </div>
        
        <div class="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
          <p>Click individual markers for more details</p>
        </div>
      </div>
    `;
  }, []);

  // Update markers with clustering
  useEffect(() => {
    const map = mapInstanceRef.current;
    const infoWindow = infoWindowRef.current;

    if (!map || !isLoaded || !infoWindow) return;

    (window as any).deliverySelectionHandler = (deliveryId: string) => {
      toggleDeliverySelection(deliveryId);
      infoWindow.close();
    };

    // Clear existing markers
    markersRef.current.forEach((marker) => {
      if (marker !== depotMarkerRef.current) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];

    if (depotMarkerRef.current) {
      depotMarkerRef.current.setMap(map);
    }

    // Group deliveries by location
    const locationGroups = groupDeliveriesByLocation(deliveriesToShow);
    
    // Track if we're at zoom level where we should show individual markers
    const currentZoom = map.getZoom();
    const showIndividualMarkers = currentZoom && currentZoom >= 15;
    
    // Create markers for each location group
    locationGroups.forEach((deliveriesAtLocation, locationKey) => {
      const positions = deliveriesAtLocation
        .map(delivery => getDeliveryPosition(delivery))
        .filter((pos): pos is google.maps.LatLngLiteral => pos !== null);
      
      if (positions.length === 0) return;
      
      const center = calculateCenter(positions);
      const isCluster = deliveriesAtLocation.length > 1;
      
      if (isCluster && !showIndividualMarkers) {
        // Create cluster marker (orange)
        const isSelected = deliveriesAtLocation.some(d => selectedDeliveries.includes(d.id));
        
        const marker = new google.maps.Marker({
          position: center,
          map,
          icon: createStarIcon("", isSelected, false, true, deliveriesAtLocation.length),
          title: `${deliveriesAtLocation.length} orders at this location`,
          zIndex: isSelected ? 1000 : 800,
        });

        let hoverTimeout: NodeJS.Timeout;

        marker.addListener("click", () => {
          // On cluster click, zoom in to show individual markers
          map.setCenter(center);
          map.setZoom(map.getZoom()! + 2);
        });

        marker.addListener("mouseover", () => {
          setHoveredDelivery(deliveriesAtLocation[0].id);

          hoverTimeout = setTimeout(() => {
            const content = createClusterInfoContent(deliveriesAtLocation);
            infoWindow.setContent(content);
            infoWindow.open(map, marker);
          }, 300);
        });

        marker.addListener("mouseout", () => {
          setHoveredDelivery(null);
          clearTimeout(hoverTimeout);

          setTimeout(() => {
            if (!deliveriesAtLocation.some(d => hoveredDelivery === d.id)) {
              infoWindow.close();
            }
          }, 100);
        });

        markersRef.current.push(marker);
      } else {
        // Create individual markers (with offset for overlapping)
        deliveriesAtLocation.forEach((delivery, index) => {
          const position = getDeliveryPosition(delivery);
          if (!position) return;

          // Apply offset for overlapping markers
          const offset = generateOffset(index, deliveriesAtLocation.length);
          const markerPosition = showIndividualMarkers ? {
            lat: position.lat + offset.lat,
            lng: position.lng + offset.lng
          } : center;

          const isSelected = selectedDeliveries.includes(delivery.id);
          const vehicle = delivery.vehicleId
            ? vehicles.find((v) => v.id === String(delivery.vehicleId))
            : null;
          const group = getDeliveryGroup(delivery.id);
          const isCollectionGroup = group?.isCollectionGrouped;

          let markerColor = "#f9f46a";

          if (delivery.status === "grouped" && group) {
            markerColor = group.color;
          } else if (delivery.status === "assigned" && vehicle) {
            markerColor = vehicle.color;
          } else if (group?.cart?.deliveryCartId) {
            const assignedCart = vehicles.find(v => v.id === group.cart!.deliveryCartId);
            if (assignedCart) {
              markerColor = assignedCart.color;
            }
          }

          // P&S orders get a blue badge — lighter blue while unassigned,
          // darker blue once grouped/assigned to a cart.
          const isPnsAssigned = markerColor !== "#f9f46a";

          const markerIcon = isPnSOrder(delivery)
            ? createPnsIcon(isSelected, isCollectionGroup, isPnsAssigned)
            : createStarIcon(markerColor, isSelected, isCollectionGroup, false);

          const marker = new google.maps.Marker({
            position: markerPosition,
            map,
            icon: markerIcon,
            title: `${
              delivery.customer?.displayName || delivery.organization?.displayName
            } - ${delivery.deliveryDetails.address}`,
            zIndex: isSelected ? 1000 : 500,
          });

          marker.addListener("click", () => {
            if (workflowStep === "group" && delivery.status === "unassigned") {
              toggleDeliverySelection(delivery.id);
            }
          });

          let hoverTimeout: NodeJS.Timeout;

          marker.addListener("mouseover", () => {
            setHoveredDelivery(delivery.id);

            hoverTimeout = setTimeout(() => {
              const content = createDeliveryInfoContent(delivery, group);
              infoWindow.setContent(content);
              infoWindow.open(map, marker);
            }, 300);
          });

          marker.addListener("mouseout", () => {
            setHoveredDelivery(null);
            clearTimeout(hoverTimeout);

            setTimeout(() => {
              if (hoveredDelivery !== delivery.id) {
                infoWindow.close();
              }
            }, 100);
          });

          google.maps.event.addListener(map, "click", () => {
            infoWindow.close();
          });

          markersRef.current.push(marker);
        });
      }
    });

    return () => {
      delete (window as any).deliverySelectionHandler;
    };
  }, [
    deliveriesToShow,
    selectedDeliveries,
    workflowStep,
    isLoaded,
    getDeliveryPosition,
    createStarIcon,
    createPnsIcon,
    isPnSOrder,
    createDeliveryInfoContent,
    createClusterInfoContent,
    toggleDeliverySelection,
    setHoveredDelivery,
    hoveredDelivery,
    getDeliveryGroup,
    vehicles,
    groupDeliveriesByLocation,
    calculateCenter,
    generateOffset,
  ]);

  // Function to draw from cached route (no API call) - UPDATED WITH deliveryCartId
  const drawCachedGroupRoute = useCallback((group: Group, route: Route, map: google.maps.Map) => {
    const isAssigned = !!group.cart?.deliveryCartId;
    
    // Use group color for both assigned and unassigned groups
     const groupColor = "#3B82F6"; 
    
    const polyline = new google.maps.Polyline({
      path: route.overview_path,
      geodesic: true,
      strokeColor: groupColor,
      strokeOpacity: isAssigned ? 0.8 : 0.6,
      strokeWeight: isAssigned ? 4 : 3,
      map,
    });

    // Set line style based on assignment status
    if (!isAssigned) {
      // Dashed line for unassigned groups
      polyline.set('strokeDashArray', [10, 5]);
    }

    polylinesRef.current.push(polyline);

    // Add animation
    const lineSymbol = {
      path: isAssigned ? google.maps.SymbolPath.FORWARD_CLOSED_ARROW : google.maps.SymbolPath.CIRCLE,
      scale: isAssigned ? 3 : 2,
      strokeColor: groupColor,
      fillColor: isAssigned ? groupColor : "transparent",
    };

    polyline.setOptions({
      icons: [
        {
          icon: lineSymbol,
          offset: '100%',
          repeat: isAssigned ? '100px' : '50px',
        },
      ],
    });

    // Store group info with polyline for reference
    (polyline as any).groupId = group.id;
    (polyline as any).isAssigned = isAssigned;
  }, []);

  // Fallback for group routes - UPDATED WITH deliveryCartId
  const drawStraightLineGroupRoute = useCallback((
    group: Group,
    deliveryPoints: google.maps.LatLngLiteral[], 
    map: google.maps.Map
  ) => {
    const path = [
      new google.maps.LatLng(DEPOT_LOCATION.lat, DEPOT_LOCATION.lng),
      ...deliveryPoints.map(point => new google.maps.LatLng(point.lat, point.lng)),
      new google.maps.LatLng(DEPOT_LOCATION.lat, DEPOT_LOCATION.lng)
    ];

    const isAssigned = !!group.cart?.deliveryCartId;
     const groupColor = "#3B82F6";

    const polyline = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: groupColor,
      strokeOpacity: isAssigned ? 0.6 : 0.4,
      strokeWeight: isAssigned ? 3 : 2,
      map,
    });

    if (!isAssigned) {
      polyline.set('strokeDashArray', [5, 5]);
    }

    polylinesRef.current.push(polyline);
  }, []);

  // Unified function to draw routes for any group (assigned or unassigned) WITH CACHING
  const drawGroupRoute = useCallback(async (group: Group, map: google.maps.Map) => {
    const deliveryPoints: google.maps.LatLngLiteral[] = [];

    // Collect delivery points for this group
    group.deliveryIds.forEach((deliveryId) => {
      const delivery = deliveries.find((d) => d.id === deliveryId);
      if (delivery && deliveriesToShow.some((d) => d.id === deliveryId)) {
        const position = getDeliveryPosition(delivery);
        if (position) {
          deliveryPoints.push(position);
        }
      }
    });

    if (deliveryPoints.length === 0) return;

    // Create cache key based on group ID and delivery points
    const cacheKey = `${group.id}-${deliveryPoints.map(p => `${p.lat},${p.lng}`).join('|')}`;
    
    // Check if we already have this route cached
    const cachedRoute = getCachedRoute(cacheKey);
    
    if (cachedRoute) {
      // Use cached route
      drawCachedGroupRoute(group, cachedRoute, map);
      return;
    }

    try {
      // Get optimized route from depot through all points and back to depot
      const route = await getRoute(
        DEPOT_LOCATION,
        DEPOT_LOCATION,
        deliveryPoints
      );

      if (route && route.overview_path.length > 0) {
        // Cache the route
        setCachedRoute(cacheKey, route);
        drawCachedGroupRoute(group, route, map);
      }
    } catch (error) {
      console.warn(`Failed to get directions for group ${group.name}, falling back to straight lines:`, error);
      drawStraightLineGroupRoute(group, deliveryPoints, map);
    }
  }, [deliveries, deliveriesToShow, getDeliveryPosition, getRoute, getCachedRoute, setCachedRoute, drawCachedGroupRoute, drawStraightLineGroupRoute]);

  // Function to draw cached individual orders
  const drawCachedIndividualOrders = useCallback((route: Route, map: google.maps.Map) => {
    // Use dotted lines for individual unassigned orders
    const polyline = new google.maps.Polyline({
      path: route.overview_path,
      geodesic: true,
       strokeColor: "#3B82F6",
      strokeOpacity: 1.0,
      strokeWeight: 2,
      map,
    });

    // Set dotted pattern for individual unassigned orders
    polyline.set('strokeDashArray', [2, 4]);

    polylinesRef.current.push(polyline);

    // Add subtle animation
    const lineSymbol = {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 1,
      strokeColor: "#3B82F6", // Blue color
    fillColor: "#3B82F6", 
    };

    polyline.setOptions({
      icons: [
        {
          icon: lineSymbol,
          offset: '100%',
          repeat: '30px',
        },
      ],
    });
  }, []);

  // Fallback for individual unassigned orders
  const drawStraightLineForIndividualOrders = useCallback((
    deliveryPoints: google.maps.LatLngLiteral[], 
    map: google.maps.Map
  ) => {
    const path = [
      new google.maps.LatLng(DEPOT_LOCATION.lat, DEPOT_LOCATION.lng),
      ...deliveryPoints.map(point => new google.maps.LatLng(point.lat, point.lng)),
      new google.maps.LatLng(DEPOT_LOCATION.lat, DEPOT_LOCATION.lng)
    ];

    const polyline = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: "#3B82F6", 
      strokeOpacity: 1.0 ,
      strokeWeight: 1,
      map,
    });

    // Set dotted pattern
    polyline.set('strokeDashArray', [2, 4]);

    polylinesRef.current.push(polyline);
  }, []);

  // Function to draw routes for individual unassigned orders (not in any group) WITH CACHING
  const drawIndividualUnassignedOrders = useCallback(async (map: google.maps.Map) => {
    // Get individual unassigned orders (not in any group)
    const individualUnassignedOrders = deliveries.filter(delivery => 
      delivery.status === "unassigned" && 
      !delivery.groupId &&
      !delivery.groupedForCollection &&
      deliveriesToShow.some(d => d.id === delivery.id)
    );

    if (individualUnassignedOrders.length === 0) return;

    const deliveryPoints: google.maps.LatLngLiteral[] = [];

    // Collect delivery points for individual unassigned orders
    individualUnassignedOrders.forEach((delivery) => {
      const position = getDeliveryPosition(delivery);
      if (position) {
        deliveryPoints.push(position);
      }
    });

    if (deliveryPoints.length === 0) return;

    // Create cache key for individual orders
    const cacheKey = `individual-${deliveryPoints.map(p => `${p.lat},${p.lng}`).join('|')}`;
    
    // Check cache
    const cachedRoute = getCachedRoute(cacheKey);
    
    if (cachedRoute) {
      drawCachedIndividualOrders(cachedRoute, map);
      return;
    }

    try {
      // Get optimized route from depot through all individual unassigned orders
      const route = await getRoute(
        DEPOT_LOCATION,
        DEPOT_LOCATION,
        deliveryPoints
      );

      if (route && route.overview_path.length > 0) {
        // Cache the route
        setCachedRoute(cacheKey, route);
        drawCachedIndividualOrders(route, map);
      }
    } catch (error) {
      console.warn('Failed to get directions for individual unassigned orders, falling back to straight lines:', error);
      // Fallback to straight lines
      drawStraightLineForIndividualOrders(deliveryPoints, map);
    }
  }, [deliveries, deliveriesToShow, getDeliveryPosition, getRoute, getCachedRoute, setCachedRoute, drawCachedIndividualOrders, drawStraightLineForIndividualOrders]);

  // Function to draw individual deliveries assigned to a specific cart - UPDATED WITH deliveryCartId
  const drawIndividualCartDeliveries = useCallback(async (cartId: string, map: google.maps.Map) => {
    const individualDeliveries = deliveries.filter(delivery => 
      delivery.vehicleId === cartId && 
      !delivery.groupId &&
      deliveriesToShow.some(d => d.id === delivery.id)
    );

    if (individualDeliveries.length === 0) return;

    const deliveryPoints: google.maps.LatLngLiteral[] = [];

    individualDeliveries.forEach((delivery) => {
      const position = getDeliveryPosition(delivery);
      if (position) {
        deliveryPoints.push(position);
      }
    });

    if (deliveryPoints.length === 0) return;

    // Create cache key
    const cacheKey = `cart-${cartId}-individual-${deliveryPoints.map(p => `${p.lat},${p.lng}`).join('|')}`;
    const cachedRoute = getCachedRoute(cacheKey);
    
    if (cachedRoute) {
      drawCachedIndividualOrders(cachedRoute, map);
      return;
    }

    try {
      const route = await getRoute(DEPOT_LOCATION, DEPOT_LOCATION, deliveryPoints);
      if (route && route.overview_path.length > 0) {
        setCachedRoute(cacheKey, route);
        drawCachedIndividualOrders(route, map);
      }
    } catch (error) {
      console.warn(`Failed to get directions for individual deliveries in cart ${cartId}:`, error);
      drawStraightLineForIndividualOrders(deliveryPoints, map);
    }
  }, [deliveries, deliveriesToShow, getDeliveryPosition, getRoute, getCachedRoute, setCachedRoute, drawCachedIndividualOrders, drawStraightLineForIndividualOrders]);

  // Main polyline drawing useEffect - UPDATED WITH deliveryCartId
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !isLoaded) return;

    // Clear previous timeout
    if (routeCalculationRef.current) {
      clearTimeout(routeCalculationRef.current);
    }

    // Debounce route calculations to prevent blinking
    routeCalculationRef.current = setTimeout(() => {
      // Only clear and redraw if the deliveriesToShow actually changed significantly
      const shouldRedraw = shouldRedrawRoutes();
      
      if (shouldRedraw) {
        polylinesRef.current.forEach((polyline) => polyline.setMap(null));
        polylinesRef.current = [];

        const drawRoutes = async () => {
          // CASE 1: Handle cart selection - UPDATED WITH deliveryCartId
          if (showOnlySelectedCarts && selectedCartsForMap.length > 0) {
            for (const cartId of selectedCartsForMap) {
              const cart = vehicles.find(v => v.id === cartId);
              if (!cart) continue;

              // Get ALL groups assigned to this cart using deliveryCartId
              const cartGroups = Object.values(groups).filter(group => 
                group.cart?.deliveryCartId === cartId
              );
              
              // Draw routes for each group
              for (const group of cartGroups) {
                await drawGroupRoute(group, map);
              }
              
              // Draw individual deliveries assigned to this cart
              await drawIndividualCartDeliveries(cartId, map);
            }
          }
          // CASE 2: Handle group selection
          else if (showOnlySelectedGroups && selectedGroupsForMap.length > 0) {
            for (const groupId of selectedGroupsForMap) {
              const group = groups[groupId];
              if (!group) continue;

              await drawGroupRoute(group, map);
            }
          }
          // CASE 3: Handle workflow steps
          else {
            // ALWAYS draw routes for ALL visible groups regardless of assignment status
            const visibleGroups = Object.values(groups).filter(group => {
              // Check if group has any deliveries that are visible
              return group.deliveryIds.some(deliveryId => 
                deliveriesToShow.some(d => d.id === deliveryId)
              );
            });

            for (const group of visibleGroups) {
              await drawGroupRoute(group, map);
            }

            // Draw individual unassigned orders in group step
            if (workflowStep === "group") {
              await drawIndividualUnassignedOrders(map);
            }
          }
        };

        drawRoutes();
      }
    }, 500); // Increased debounce to 500ms

    return () => {
      if (routeCalculationRef.current) {
        clearTimeout(routeCalculationRef.current);
      }
    };
  }, [
    isLoaded,
    deliveriesToShow,
    workflowStep,
    showOnlySelectedCarts,
    selectedCartsForMap,
    showOnlySelectedGroups,
    selectedGroupsForMap,
    groups,
    vehicles,
    drawGroupRoute,
    drawIndividualUnassignedOrders,
    drawIndividualCartDeliveries,
    shouldRedrawRoutes,
  ]);

  // Clear cache when the main data changes significantly
  useEffect(() => {
    clearCache();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveries.length, Object.keys(groups).length, clearCache]);

  // Clear cache when component unmounts
  useEffect(() => {
    return () => {
      clearCache();
    };
  }, [clearCache]);

  // Add zoom change listener to update markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const zoomListener = map.addListener('zoom_changed', () => {
      // Trigger marker recreation when zoom changes
      setTimeout(() => {
        // Force re-render of markers based on new zoom level
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        // The marker useEffect will recreate markers with the new zoom level
      }, 100);
    });

    return () => {
      google.maps.event.removeListener(zoomListener);
    };
  }, []);

  // Map control functions
  const zoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom()! + 1);
    }
  };

  const zoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom()! - 1);
    }
  };

  const resetView = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setCenter(DEFAULT_CENTER);
      mapInstanceRef.current.setZoom(DEFAULT_ZOOM);
    }
  };

  const fitBounds = () => {
    const map = mapInstanceRef.current;
    if (!map || deliveriesToShow.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    bounds.extend(
      new google.maps.LatLng(DEPOT_LOCATION.lat, DEPOT_LOCATION.lng)
    );

    deliveriesToShow.forEach((delivery) => {
      const position = getDeliveryPosition(delivery);
      if (position) {
        bounds.extend(new google.maps.LatLng(position.lat, position.lng));
      }
    });

    map.fitBounds(bounds);
  };

  // Update map title based on current filters
  const getMapTitle = () => {
    let title = "Delivery Routes Map";

    if (showOnlySelectedOrders && selectedOrdersForMap.length > 0) {
      title = `Selected Orders (${selectedOrdersForMap.length})`;
    } else if (showOnlySelectedCarts && selectedCartsForMap.length > 0) {
      // DIFFERENT TITLES BASED ON CONTEXT
      if (workflowStep === "assign" && effectiveTentativeFilter === "assigned") {
        title = `Selected Carts - Tentative Groups (${selectedCartsForMap.length} carts)`;
      } else {
        title = `Selected Carts - All Groups (${selectedCartsForMap.length} carts)`;
      }
    } else if (showOnlySelectedGroups && selectedGroupsForMap.length > 0) {
      title = `Selected Groups (${selectedGroupsForMap.length})`;
    } else if (workflowStep === "actual") {
      title = "Cart Assigned Groups Map";
    } else if (workflowStep === "assign") {
      if (effectiveGroupTypeFilter === "finalized") {
        title = "Finalized Group Orders Map";
      } else {
        switch (effectiveTentativeFilter) {
          case "all":
            title = "All Tentative Groups Map";
            break;
          case "assigned":
            title = "Cart Assigned Groups Map (Tentative Only)";
            break;
          case "unassigned":
            title = "Unassigned Groups Map";
            break;
        }
      }
    } else if (workflowStep === "group") {
      title = "Orders & Unassigned Groups Map";
    }

    return title;
  };

  if (!isLoaded) {
    return (
      <div className="google-map-view">
        <div className="google-map-view__container">
          <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading Google Maps...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="google-map-view">
      <div className="google-map-view__header">
        <h2 className="google-map-view__title">
          <MapIcon size={20} />
          {getMapTitle()}
          {showOnlySelectedOrders && selectedOrdersForMap.length > 0 && (
            <span className="google-map-view__selected-count">
              ({selectedOrdersForMap.length} selected)
            </span>
          )}
          {showOnlySelectedCarts && selectedCartsForMap.length > 0 && (
            <span className="google-map-view__selected-count">
              ({selectedCartsForMap.length} carts selected)
            </span>
          )}
          {showOnlySelectedGroups && selectedGroupsForMap.length > 0 && (
            <span className="google-map-view__selected-count">
              ({selectedGroupsForMap.length} groups selected)
            </span>
          )}
        </h2>
        <div className="google-map-view__controls">
          <button
            onClick={zoomIn}
            className="google-map-view__control-button"
            title="Zoom In"
          >
            <ZoomIn size={16} />
          </button>
          <button
            onClick={zoomOut}
            className="google-map-view__control-button"
            title="Zoom Out"
          >
            <ZoomOut size={16} />
          </button>
          <button
            onClick={resetView}
            className="google-map-view__control-button"
            title="Reset View"
          >
            <Navigation size={16} />
          </button>
          <button
            onClick={fitBounds}
            className="google-map-view__control-button google-map-view__control-button--primary"
          >
            Fit All Markers
          </button>
        </div>
      </div>

      <div className="google-map-view__container">
        <div
          ref={mapRef}
          className="google-map-view__map"
          style={{ height: "600px", width: "100%" }}
        />
      </div>
    </div>
  );
};

export default GoogleMapView;